import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { getQueuesLOV, type QueueSummary } from 'api/queues/endpoints/getQueuesLOV';

import { EditReference } from './EditReference';

interface Props {
  // The stored `zone_name|queue_name` path, or null for "no queue" / use the default.
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

// `queues.queue_name` is a nullable column. The coalesces are not decoration: `getKey` builds the composite path that is
// STORED in the node config, so an interpolated null would write the literal text "null" into saved data. `?? ''` keeps
// what a null produced before the declaration was corrected (`str()` substituted `''`), so nothing that round-trips
// changes shape here.
// Exported so the null handling can be pinned directly: this is the value that gets SAVED, and a view test would not
// reach it (the path is only observable after a selection round-trip).
export const queuePathOf = (q: QueueSummary) => `${q.zone_name}|${q.queue_name ?? ''}`;
const getKey = queuePathOf;
const getLabel = (q: QueueSummary): string => `${q.queue_name ?? ''} (${q.zone_name})`;

/**
 * Searchable dropdown for queues keyed on the `zone_name|queue_name` path — the shape the node config stores. Unlike the
 * id-keyed references, the stored value already carries both display parts, so the interim item built by splitting it has a
 * real label: the field reads correctly before the eager LOV even loads (the LOV only populates the option list). Selection
 * returns the composite path (or null when cleared).
 */
export const EditQueueReference = ({ value, onChange, label = 'Queue', mandatory, disabled }: Props) => {
  // Split the stored path into the partial summary EditReference selects on. Memoised on the path so the reference model
  // isn't rebuilt each render.
  const selected = useMemo<QueueSummary | null>(() => {
    if (value === null || value === '') return null;
    const [zone_name = '', queue_name = ''] = value.split('|');
    return { queue_id: 0, queue_name, zone_name };
  }, [value]);

  return (
    <EditReference<QueueSummary>
      label={label}
      value={selected}
      onChange={(q) => onChange(q !== null ? getKey(q) : null)}
      source={getQueuesLOV}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(q, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{q.queue_name ?? ''}</Highlighter>
          </span>
          <span className="text-body-secondary">{q.zone_name}</span>
        </div>
      )}
    />
  );
};
